/**
 * Wochenraster.
 *
 * Zeilen sind Standort/Position, Spalten die sieben Tage. Wer plant, denkt in
 * „wer steht Freitag an der Bar" — nicht in „welche Termine hat Person X".
 * Die persönliche Sicht kommt in Phase 3 dazu.
 *
 * Drag & Drop mit dnd-kit, **inklusive Tastatur**: Ohne den KeyboardSensor
 * wäre die Planung für alle verschlossen, die keine Maus benutzen können, und
 * das ist bei einem Werkzeug für die Personaleinsatzplanung keine Randgruppe,
 * sondern ein Ausschluss.
 */

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useMemo, useState } from 'react'

import {
  api,
  ApiError,
  SEVERITY_TONE,
  type AssignResult,
  type DayCost,
  type ListResponse,
  type Location,
  type Me,
  type Member,
  type PlanConflict,
  type Position,
  type Shift,
} from '../lib/api'
import { Calendar, escapeHtml, type CalendarView } from '../components/Calendar'
import { addDays, isoDate, mondayOf, monthGridRange, weekRange } from '../lib/calendar-range'
import { oneOf, usePersistentState } from '../lib/persistent-state'
import { renderShiftEvent, shiftsToEvents, shiftsToResources } from '../lib/calendar-events'
import { springSnappy, springSoft } from '../lib/motion'
import { Banner, Button, Card, Chip, Dialog, Spinner, TextField, useToast } from '../ui'
import './plan.css'

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

/**
 * Darstellung des Plans. `grid` ist das ursprüngliche Raster mit dnd-kit — es
 * bleibt, weil nur dort die Einteilung per Tastatur funktioniert. Der Kalender
 * kann Termine bislang nur mit dem Zeiger verschieben.
 */
const PLAN_ANSICHTEN = [
  'resourceTimelineDay',
  'timeGridWeek',
  'dayGridMonth',
  'listWeek',
  'grid',
] as const

type PlanAnsicht = (typeof PLAN_ANSICHTEN)[number]

// Zusicherung beim Übersetzen: Alles außer `grid` muss eine Ansicht sein, die
// der Kalender kennt. Ein Tippfehler in der Liste oben fiele sonst erst zur
// Laufzeit auf — und dann als leerer Kalender, nicht als Fehlermeldung.
type _AnsichtenSindGueltig = Exclude<PlanAnsicht, 'grid'> extends CalendarView ? true : never
const _pruefung: _AnsichtenSindGueltig = true
void _pruefung

/**
 * Index des heutigen Tages in der Woche (Montag = 0).
 *
 * Die Timeline soll mit dem Tag aufmachen, für den man sich am ehesten
 * interessiert. Startete sie immer montags, sähe man in Betrieben mit
 * Ruhetag am Montag zuerst eine leere Fläche.
 */
function heutigerWochentag(): number {
  const tag = new Date().getDay()
  return tag === 0 ? 6 : tag - 1
}

/**
 * Zeitraum, den eine Ansicht tatsächlich zeigt.
 *
 * ⚠️ Nicht immer die Woche. Das Monatsraster stellt vier bis sechs Wochen dar;
 * lädt man nur die laufende, fehlen im Bild sämtliche Schichten davor und
 * danach — und zwar **ohne jeden Hinweis**. Der Plan sieht dann leer aus, wo er
 * voll ist, und genau das ist die gefährlichste Sorte Anzeigefehler.
 *
 * Das Monatsraster beginnt am Montag der Woche, in der der Erste liegt, und
 * läuft über sechs volle Wochen — mehr kann es nie sein.
 */
function sichtbarerBereich(ansicht: PlanAnsicht, monday: Date): { von: Date; bis: Date } {
  return ansicht === 'dayGridMonth' ? monthGridRange(monday) : weekRange(monday)
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

export function PlanPage({ me }: { me: Me }) {
  const tenant = me.active_tenant
  const canPlan = tenant?.role === 'owner' || tenant?.role === 'manager'
  const queryClient = useQueryClient()
  const toast = useToast()

  const [monday, setMonday] = useState(() => mondayOf(new Date()))
  // Der Kalender ist die Standardansicht; das tastaturbedienbare Raster bleibt
  // als eigene Ansicht erreichbar. Die Wahl überdauert einen Neuladen nicht —
  // sie ist eine Vorliebe des Augenblicks, keine Einstellung.
  // Übersteht das Neuladen: Wer die Timeline eingestellt hat, will nach F5
  // nicht wieder im Monatsraster landen.
  const [ansicht, setAnsicht] = usePersistentState<PlanAnsicht>(
    'plan.ansicht',
    'resourceTimelineDay',
    oneOf(PLAN_ANSICHTEN),
  )
  const [detail, setDetail] = useState<string | null>(null)
  /**
   * Welcher Tag in der Timeline steht.
   *
   * Die Timeline zeigt bewusst **einen** Tag: Eine ganze Woche mit
   * Ressourcenzeilen auf einen Bildschirm zu quetschen lässt je Schicht rund
   * 35 Pixel — dort steht dann „19…" statt eines Namens. Mit Stundenspalten
   * bekommt eine Sechs-Stunden-Schicht dagegen reichlich Platz.
   */
  const [timelineTag, setTimelineTag] = useState(() => heutigerWochentag())
  const [pending, setPending] = useState<{ shift: Shift; membershipId: string } | null>(null)

  // ⚠️ Zwei Zeiträume, und sie dürfen nicht verwechselt werden:
  //
  //   `from`/`to`       — was GELADEN und im Kalender gezeigt wird. Hängt an
  //                       der Ansicht; im Monatsraster sechs Wochen.
  //   `wocheVon`/`-Bis` — die dargestellte KALENDERWOCHE. Daran hängen alle
  //                       wochenbezogenen Aktionen.
  //
  // Würden „Woche veröffentlichen" oder „Vorwoche übernehmen" den geladenen
  // Bereich benutzen, veröffentlichte ein Klick im Monatsraster sechs Wochen
  // auf einmal — und der Knopf verspräche etwas anderes, als er tut.
  const bereich = sichtbarerBereich(ansicht, monday)
  const from = isoDate(bereich.von)
  const to = isoDate(bereich.bis)
  const wocheVon = isoDate(monday)
  const wocheBis = isoDate(addDays(monday, 6))
  const isOwner = tenant?.role === 'owner'

  const [shifts, locations, positions, members] = useQueries({
    queries: [
      {
        queryKey: ['shifts', tenant?.id, from],
        queryFn: () => api.get<ListResponse<Shift>>(`/api/shifts?from=${from}&to=${to}`),
        enabled: Boolean(tenant),
      },
      {
        queryKey: ['locations', tenant?.id],
        queryFn: () => api.get<ListResponse<Location>>('/api/locations'),
        enabled: Boolean(tenant),
      },
      {
        queryKey: ['positions', tenant?.id],
        queryFn: () => api.get<ListResponse<Position>>('/api/positions'),
        enabled: Boolean(tenant),
      },
      {
        queryKey: ['members', tenant?.id],
        queryFn: () => api.get<ListResponse<Member>>('/api/members'),
        enabled: Boolean(tenant),
      },
    ],
  })

  const assign = useMutation({
    mutationFn: (input: { shiftId: string; membershipId: string; reason?: string }) =>
      api.post<AssignResult>(`/api/shifts/${input.shiftId}/assignments`, {
        membership_id: input.membershipId,
        reason: input.reason ?? null,
      }),
    onSuccess: (result, input) => {
      if (result.needs_reason) {
        // Der Server hat nichts gespeichert und verlangt eine Begründung.
        const shift = (shifts.data?.items ?? []).find((s) => s.id === input.shiftId)
        if (shift) setPending({ shift, membershipId: input.membershipId })
        return
      }
      queryClient.invalidateQueries({ queryKey: ['shifts'] })
      setPending(null)
      if (result.conflicts.length > 0) {
        toast(`Eingeteilt — mit ${result.conflicts.length} Hinweis(en).`, 'neutral')
      } else {
        toast('Eingeteilt.', 'success')
      }
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const unassign = useMutation({
    mutationFn: (assignmentId: string) => api.delete(`/api/assignments/${assignmentId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shifts'] }),
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  /**
   * Schicht im Kalender verschieben oder in der Länge ändern.
   *
   * Bewusst keine Mutation, sondern ein direkter Aufruf: Der Kalender reicht
   * ein `revert` mit, das den Termin an seine alte Stelle zurücksetzt. Lehnt
   * der Server ab, muss genau dieses `revert` laufen — sonst liegt der Termin
   * dort, wo er nie gespeichert wurde, und beim nächsten Laden springt er
   * kommentarlos zurück.
   */
  const verschieben = async (
    shiftId: string,
    start: Date,
    end: Date,
    revert: () => void,
    neueZeile?: string,
  ) => {
    // Die Zeilen-ID der Timeline ist „standort|position". Beim Zug in eine
    // andere Zeile ändert sich also beides — wer nur die Zeiten schickt,
    // speichert etwas anderes, als gerade zu sehen war.
    const [locationId, positionId] = neueZeile?.split('|') ?? []
    const alt = (shifts.data?.items ?? []).find((s) => s.id === shiftId)
    const positionWechselt = Boolean(positionId && alt && positionId !== alt.position_id)

    try {
      await api.patch(`/api/shifts/${shiftId}`, {
        starts_at: start.toISOString(),
        ends_at: end.toISOString(),
        ...(locationId ? { location_id: locationId } : {}),
        ...(positionId ? { position_id: positionId } : {}),
      })
      queryClient.invalidateQueries({ queryKey: ['shifts'] })
      toast(
        positionWechselt
          ? 'Verschoben — bitte die Qualifikationen der Eingeteilten prüfen.'
          : 'Schicht verschoben.',
        positionWechselt ? 'neutral' : 'success',
      )
    } catch (error) {
      revert()
      toast((error as ApiError).message, 'error')
    }
  }

  const publish = useMutation({
    mutationFn: () => api.post<{ published: number; changed: number; notified: number }>(
      '/api/plan/publish',
      { start_on: wocheVon, end_on: wocheBis },
    ),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] })
      toast(
        `${result.published} Schicht(en) veröffentlicht, ${result.notified} Person(en) benachrichtigt.`,
        'success',
      )
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const copyWeek = useMutation({
    mutationFn: () =>
      api.post<{ copied: number }>('/api/plan/copy-week', {
        source_monday: isoDate(addDays(monday, -7)),
        target_monday: wocheVon,
        with_assignments: true,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] })
      toast(`${result.copied} Schicht(en) aus der Vorwoche übernommen.`, 'success')
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  // Tastatur ist gleichwertig, nicht nachgereicht.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor))

  const byDayAndRow = useMemo(() => {
    const map = new Map<string, Shift[]>()
    for (const shift of shifts.data?.items ?? []) {
      const day = isoDate(new Date(shift.starts_at))
      const key = `${shift.location_id}|${shift.position_id}|${day}`
      map.set(key, [...(map.get(key) ?? []), shift])
    }
    return map
  }, [shifts.data])

  const rows = useMemo(() => {
    const combos = new Set<string>()
    for (const shift of shifts.data?.items ?? []) {
      combos.add(`${shift.location_id}|${shift.position_id}`)
    }
    return [...combos].map((key) => {
      const [locationId, positionId] = key.split('|')
      return {
        key,
        location: locations.data?.items.find((l) => l.id === locationId),
        position: positions.data?.items.find((p) => p.id === positionId),
      }
    })
  }, [shifts.data, locations.data, positions.data])

  // Die Übersetzung liegt in `lib/calendar-events` — beide Sichten brauchen
  // sie, und dort ist sie ohne DOM prüfbar.
  const kalenderTermine = useMemo(
    () =>
      shiftsToEvents(shifts.data?.items ?? [], {
        positions: positions.data?.items ?? [],
      }).map((termin: ReturnType<typeof shiftsToEvents>[number]) => ({
        ...termin,
        // Die Klassen wandern aus den extendedProps in das Feld, das die
        // Bibliothek auswertet.
        classNames: (termin.extendedProps?.klassen as string[]) ?? [],
      })),
    [shifts.data, positions.data],
  )

  const kalenderZeilen = useMemo(
    () =>
      shiftsToResources(
        shifts.data?.items ?? [],
        locations.data?.items ?? [],
        positions.data?.items ?? [],
      ),
    [shifts.data, locations.data, positions.data],
  )

  if (!tenant) return <Card title="Kein Betrieb ausgewählt">Wähle oben einen Betrieb.</Card>
  if (shifts.isPending) return <Spinner label="Plan wird geladen …" />

  const onDragEnd = (event: DragEndEvent) => {
    const membershipId = String(event.active.id)
    const shiftId = event.over ? String(event.over.id) : null
    if (!shiftId) return
    assign.mutate({ shiftId, membershipId })
  }

  // Nur die Entwürfe DIESER Woche — der Knopf daneben veröffentlicht auch nur
  // sie. Im Monatsraster sind mehr geladen; die mitzuzählen hieße, eine Zahl
  // neben einen Knopf zu stellen, der etwas anderes tut.
  const drafts = (shifts.data?.items ?? []).filter(
    (s) => !s.is_published && isoDate(new Date(s.starts_at)) >= wocheVon
      && isoDate(new Date(s.starts_at)) <= wocheBis,
  ).length

  return (
    <div className="stack">
      <div className="row row--between">
        <h1>Plan</h1>
        <div className="row">
          <Button
            variant="text"
            small
            onClick={() => {
              setMonday(addDays(monday, -7))
              setTimelineTag(0)
            }}
          >
            ← Vorwoche
          </Button>
          <strong className="plan__week">
            {monday.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} –{' '}
            {addDays(monday, 6).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </strong>
          <Button
            variant="text"
            small
            onClick={() => {
              setMonday(addDays(monday, 7))
              // In einer anderen Woche gibt es kein „heute" — dort ist der
              // erste Tag die naheliegende Wahl.
              setTimelineTag(0)
            }}
          >
            Folgewoche →
          </Button>
        </div>
      </div>

      {canPlan && (
        <div className="row row--between">
          <div className="row">
            <Button variant="outlined" small busy={copyWeek.isPending} onClick={() => copyWeek.mutate()}>
              Vorwoche übernehmen
            </Button>
            <NewShiftButton
              locations={locations.data?.items ?? []}
              positions={positions.data?.items ?? []}
              monday={monday}
            />
          </div>
          <div className="row">
            {drafts > 0 && <Chip tone="warning">{drafts} im Entwurf</Chip>}
            <Button small busy={publish.isPending} disabled={drafts === 0} onClick={() => publish.mutate()}>
              Woche veröffentlichen
            </Button>
          </div>
        </div>
      )}

      <div className="row" role="tablist" aria-label="Darstellung">
        {(
          [
            ['resourceTimelineDay', 'Timeline'],
            ['timeGridWeek', 'Woche'],
            ['dayGridMonth', 'Monat'],
            ['listWeek', 'Liste'],
            ['grid', 'Raster'],
          ] as const
        ).map(([wert, label]) => (
          <Button
            key={wert}
            variant={ansicht === wert ? 'tonal' : 'text'}
            small
            role="tab"
            aria-selected={ansicht === wert}
            onClick={() => setAnsicht(wert)}
          >
            {label}
          </Button>
        ))}
      </div>

      {ansicht !== 'grid' && (
        <Card>
          {ansicht === 'resourceTimelineDay' && (
            <div
              className="row"
              role="tablist"
              aria-label="Tag"
              style={{ marginBottom: 'var(--sp-3)' }}
            >
              {WEEKDAYS.map((label, index) => {
                const tag = addDays(monday, index)
                const heute = isoDate(tag) === isoDate(new Date())
                return (
                  <Button
                    key={label}
                    variant={timelineTag === index ? 'tonal' : 'text'}
                    small
                    role="tab"
                    aria-selected={timelineTag === index}
                    onClick={() => setTimelineTag(index)}
                  >
                    {label} {tag.getDate()}.{heute ? ' •' : ''}
                  </Button>
                )
              })}
            </div>
          )}
          <Calendar
            view={ansicht}
            date={ansicht === 'resourceTimelineDay' ? addDays(monday, timelineTag) : monday}
            events={kalenderTermine}
            resources={kalenderZeilen}
            editable={canPlan}
            // Die Timeline wächst mit der Zahl der Zeilen; eine feste Höhe
            // ließe unter drei Positionen ein halbes Bild Leerfläche stehen.
            // Die Timeline wächst mit der Zahl der Zeilen; eine feste Höhe
            // ließe unter drei Positionen ein halbes Bild Leerfläche stehen.
            height={
              ansicht === 'dayGridMonth'
                ? '720px'
                : ansicht === 'resourceTimelineDay'
                  ? 'auto'
                  : '640px'
            }
            renderEvent={(info) => renderShiftEvent(info, escapeHtml)}
            onEventClick={(id) => setDetail(id)}
            onEventDrop={verschieben}
            onEventResize={verschieben}
          />
          <p className="muted" style={{ fontSize: '0.8125rem', margin: 0 }}>
            Gestrichelt = Entwurf, roter Rahmen = unterbesetzt.
            {canPlan && ' Ziehen verschiebt die Schicht, an den Rändern ziehen ändert die Dauer.'}
            {' '}Für die Einteilung per Tastatur steht die Ansicht „Raster" bereit.
          </p>
        </Card>
      )}

      <ShiftDetailDialog
        shift={(shifts.data?.items ?? []).find((s) => s.id === detail) ?? null}
        locations={locations.data?.items ?? []}
        positions={positions.data?.items ?? []}
        canPlan={canPlan}
        onClose={() => setDetail(null)}
        onUnassign={(assignmentId) => unassign.mutate(assignmentId)}
      />

      {ansicht === 'grid' && (
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="plan">
          <div className="plan__grid" role="table" aria-label="Wochenplan">
            <div className="plan__cell plan__cell--head" role="columnheader">
              Standort · Position
            </div>
            {WEEKDAYS.map((label, index) => {
              const day = addDays(monday, index)
              return (
                <div key={label} className="plan__cell plan__cell--head" role="columnheader">
                  {label} {day.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                </div>
              )
            })}

            {rows.length === 0 && (
              <div className="plan__empty">
                Für diese Woche sind noch keine Schichten angelegt.
                {canPlan && ' Lege eine an oder übernimm die Vorwoche.'}
              </div>
            )}

            {rows.map((row) => (
              <PlanRow
                key={row.key}
                row={row}
                monday={monday}
                byDayAndRow={byDayAndRow}
                canPlan={canPlan}
                onRemove={(id) => unassign.mutate(id)}
              />
            ))}
          </div>

          {canPlan && <MemberRail members={members.data?.items ?? []} />}
        </div>
      </DndContext>
      )}

      <OverrideDialog
        pending={pending}
        onClose={() => setPending(null)}
        onConfirm={(reason) =>
          pending &&
          assign.mutate({ shiftId: pending.shift.id, membershipId: pending.membershipId, reason })
        }
        busy={assign.isPending}
      />

      {isOwner && <LaborCostCard from={wocheVon} to={wocheBis} />}
    </div>
  )
}

// ------------------------------------------------------------------- Zeile --

type Row = { key: string; location?: Location; position?: Position }

function PlanRow({
  row,
  monday,
  byDayAndRow,
  canPlan,
  onRemove,
}: {
  row: Row
  monday: Date
  byDayAndRow: Map<string, Shift[]>
  canPlan: boolean
  onRemove: (assignmentId: string) => void
}) {
  return (
    <>
      <div className="plan__cell plan__cell--row-head" role="rowheader">
        <span className="plan__dot" style={{ background: row.location?.color ?? '#888' }} />
        <span>
          <strong>{row.position?.name ?? 'Position'}</strong>
          <br />
          <span className="muted">{row.location?.name ?? 'Standort'}</span>
        </span>
      </div>
      {Array.from({ length: 7 }, (_, index) => {
        const day = isoDate(addDays(monday, index))
        const shifts = byDayAndRow.get(`${row.key}|${day}`) ?? []
        return (
          <div key={day} className="plan__cell" role="cell">
            {shifts.map((shift) => (
              <ShiftCard key={shift.id} shift={shift} canPlan={canPlan} onRemove={onRemove} />
            ))}
          </div>
        )
      })}
    </>
  )
}

function ShiftCard({
  shift,
  canPlan,
  onRemove,
}: {
  shift: Shift
  canPlan: boolean
  onRemove: (assignmentId: string) => void
}) {
  const { setNodeRef, isOver } = useDroppable({ id: shift.id, disabled: !canPlan })
  const understaffed = shift.assignments.length < shift.min_staff

  return (
    <motion.div
      ref={setNodeRef}
      layout
      transition={springSnappy()}
      className={[
        'shift',
        shift.is_published ? 'shift--published' : 'shift--draft',
        isOver ? 'shift--over' : '',
        understaffed ? 'shift--understaffed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="shift__time">
        {timeLabel(shift.starts_at)}–{timeLabel(shift.ends_at)}
        {!shift.is_published && <span className="shift__badge">Entwurf</span>}
      </div>
      <ul className="shift__people">
        <AnimatePresence initial={false}>
          {shift.assignments.map((assignment) => (
            <motion.li
              key={assignment.id}
              layout
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={springSoft()}
              title={assignment.override_reason ?? undefined}
            >
              <span>{assignment.full_name}</span>
              {assignment.override_reason && (
                <span className="shift__override" aria-label="Mit Begründung eingeteilt">
                  !
                </span>
              )}
              {canPlan && (
                <button
                  className="shift__remove"
                  aria-label={`${assignment.full_name} entfernen`}
                  onClick={() => onRemove(assignment.id)}
                >
                  ×
                </button>
              )}
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
      <div className="shift__foot muted">
        {shift.assignments.length}/{shift.min_staff}
        {shift.max_staff !== shift.min_staff && `–${shift.max_staff}`}
        {understaffed && <span className="shift__gap">unterbesetzt</span>}
      </div>
    </motion.div>
  )
}

// ------------------------------------------------------------ Personalleiste

function MemberRail({ members }: { members: Member[] }) {
  return (
    <aside className="rail" aria-label="Belegschaft">
      <h3 className="rail__title">Team</h3>
      <p className="muted rail__hint">
        Auf eine Schicht ziehen. Mit der Tastatur: anwählen, Leertaste, Pfeiltasten, Leertaste.
      </p>
      {members.map((member) => (
        <DraggableMember key={member.membership_id} member={member} />
      ))}
    </aside>
  )
}

function DraggableMember({ member }: { member: Member }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: member.membership_id,
  })
  return (
    <div
      ref={setNodeRef}
      className={`rail__person ${isDragging ? 'rail__person--dragging' : ''}`}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      {...listeners}
      {...attributes}
    >
      {member.full_name}
    </div>
  )
}

// ---------------------------------------------------------- Übersteuerung ---

function OverrideDialog({
  pending,
  onClose,
  onConfirm,
  busy,
}: {
  pending: { shift: Shift; membershipId: string } | null
  onClose: () => void
  onConfirm: (reason: string) => void
  busy: boolean
}) {
  const [reason, setReason] = useState('')
  const [conflicts, setConflicts] = useState<PlanConflict[]>([])

  // Die Konflikte holt der Dialog selbst per Probelauf — so steht immer der
  // aktuelle Stand da und nicht der von vor dem letzten Klick.
  const probe = useQuery({
    queryKey: ['assign-probe', pending?.shift.id, pending?.membershipId],
    queryFn: async () => {
      const result = await api.post<AssignResult>(
        `/api/shifts/${pending!.shift.id}/assignments`,
        { membership_id: pending!.membershipId, dry_run: true },
      )
      setConflicts(result.conflicts)
      return result
    },
    enabled: Boolean(pending),
  })

  return (
    <Dialog open={Boolean(pending)} onClose={onClose} title="Trotz Warnung einteilen?">
      <div className="stack">
        {probe.isPending && <Spinner label="Wird geprüft …" />}
        {conflicts.map((conflict) => (
          <Banner key={conflict.code} tone={conflict.severity === 'legal' ? 'error' : 'warning'}>
            <div>
              <Chip tone={SEVERITY_TONE[conflict.severity]}>
                {conflict.severity === 'legal' ? 'Gesetzliche Grenze' : 'Hinweis'}
              </Chip>{' '}
              {conflict.message}
            </div>
          </Banner>
        ))}
        <TextField
          label="Begründung"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint="Wird im Protokoll festgehalten. Sie ist der Grund, warum das hier möglich ist."
        />
        <div className="row row--between">
          <Button variant="text" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={reason.trim().length < 5}
            onClick={() => onConfirm(reason.trim())}
          >
            Trotzdem einteilen
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// ---------------------------------------------------------- Neue Schicht ----

function NewShiftButton({
  locations,
  positions,
  monday,
}: {
  locations: Location[]
  positions: Position[]
  monday: Date
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    location_id: '',
    position_id: '',
    day: isoDate(monday),
    start: '17:00',
    end: '23:00',
    break_minutes: 30,
    min_staff: 1,
    max_staff: 2,
  })

  const create = useMutation({
    mutationFn: () => {
      // Die Eingabe ist Ortszeit. `new Date('2026-03-10T17:00')` deutet einen
      // Zeitstempel ohne Zone als lokale Zeit — genau das ist hier gewollt,
      // der Browser steht im selben Betrieb.
      const starts = new Date(`${form.day}T${form.start}`)
      const ends = new Date(`${form.day}T${form.end}`)
      if (ends <= starts) ends.setDate(ends.getDate() + 1) // über Mitternacht
      return api.post('/api/shifts', {
        location_id: form.location_id,
        position_id: form.position_id,
        starts_at: starts.toISOString(),
        ends_at: ends.toISOString(),
        break_minutes: Number(form.break_minutes),
        min_staff: Number(form.min_staff),
        max_staff: Number(form.max_staff),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] })
      toast('Schicht angelegt.', 'success')
      setOpen(false)
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const ready = form.location_id && form.position_id

  return (
    <>
      <Button small onClick={() => setOpen(true)} disabled={locations.length === 0 || positions.length === 0}>
        Schicht anlegen
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Neue Schicht">
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault()
            create.mutate()
          }}
        >
          <div className="grid-2">
            <label className="field">
              <span className="field__label">Standort</span>
              <select
                className="field__control"
                value={form.location_id}
                onChange={(e) => setForm({ ...form, location_id: e.target.value })}
              >
                <option value="">Bitte wählen</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Position</span>
              <select
                className="field__control"
                value={form.position_id}
                onChange={(e) => setForm({ ...form, position_id: e.target.value })}
              >
                <option value="">Bitte wählen</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <TextField
              label="Tag"
              type="date"
              value={form.day}
              onChange={(e) => setForm({ ...form, day: e.target.value })}
            />
            <TextField
              label="Pause (Minuten)"
              type="number"
              min={0}
              max={480}
              value={form.break_minutes}
              onChange={(e) => setForm({ ...form, break_minutes: Number(e.target.value) })}
              hint="Ab 6 h sind 30 vorgeschrieben, ab 9 h 45."
            />
            <TextField
              label="Beginn"
              type="time"
              value={form.start}
              onChange={(e) => setForm({ ...form, start: e.target.value })}
            />
            <TextField
              label="Ende"
              type="time"
              value={form.end}
              onChange={(e) => setForm({ ...form, end: e.target.value })}
              hint="Vor dem Beginn = über Mitternacht."
            />
            <TextField
              label="Soll"
              type="number"
              min={0}
              max={99}
              value={form.min_staff}
              onChange={(e) => setForm({ ...form, min_staff: Number(e.target.value) })}
            />
            <TextField
              label="Höchstens"
              type="number"
              min={1}
              max={99}
              value={form.max_staff}
              onChange={(e) => setForm({ ...form, max_staff: Number(e.target.value) })}
            />
          </div>
          <div className="row row--between">
            <Button variant="text" type="button" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" busy={create.isPending} disabled={!ready}>
              Anlegen
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  )
}

// --------------------------------------------------------- Personalkosten

/**
 * Geplante Personalkosten und Personalkostenquote der Woche.
 *
 * **Nur für den Inhaber.** Die Zahlen stammen aus einzelnen Stundenlöhnen; in
 * einem kleinen Betrieb lässt sich aus einer Tagessumme der Einzelwert
 * zurückrechnen, sobald man die übrigen kennt. Der Server weist andere Rollen
 * ohnehin ab — das Ausblenden hier erspart die Fehlermeldung.
 */
function LaborCostCard({ from, to }: { from: string; to: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [editing, setEditing] = useState<string | null>(null)
  const [wert, setWert] = useState('')

  const kosten = useQuery({
    queryKey: ['labor-cost', from, to],
    queryFn: () => api.get<ListResponse<DayCost>>(`/api/reports/labor-cost?from=${from}&to=${to}`),
  })

  const speichern = useMutation({
    mutationFn: ({ tag, betrag }: { tag: string; betrag: string }) =>
      api.put(`/api/revenue/${tag}`, { planned_revenue: betrag.trim() || null }),
    onSuccess: () => {
      toast('Umsatz gespeichert.', 'success')
      queryClient.invalidateQueries({ queryKey: ['labor-cost'] })
      setEditing(null)
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  if (kosten.isPending) return null
  const tage = kosten.data?.items ?? []

  const summeStunden = tage.reduce((s, t) => s + Number(t.planned_hours), 0)
  const vollstaendig = tage.every((t) => t.missing_wages === 0)
  const summeKosten = vollstaendig
    ? tage.reduce((s, t) => s + Number(t.planned_cost ?? 0), 0)
    : null
  const summeUmsatz = tage.reduce(
    (s, t) => s + Number(t.actual_revenue ?? t.planned_revenue ?? 0),
    0,
  )
  const quote = summeKosten !== null && summeUmsatz > 0 ? (summeKosten / summeUmsatz) * 100 : null

  return (
    <Card title="Personalkosten (Plan)">
      <div className="grid-2">
        <div className="stat">
          <span className="stat__value">{summeStunden.toFixed(1)} h</span>
          <span className="stat__label">Geplante Stunden</span>
        </div>
        <div className="stat">
          <span className="stat__value">
            {summeKosten === null ? '–' : `${summeKosten.toFixed(2)} €`}
          </span>
          <span className="stat__label">Personalkosten</span>
        </div>
        <div className="stat">
          <span className="stat__value">
            {summeUmsatz > 0 ? `${summeUmsatz.toFixed(2)} €` : '–'}
          </span>
          <span className="stat__label">Umsatz</span>
        </div>
        <div className="stat">
          <span className="stat__value">{quote === null ? '–' : `${quote.toFixed(1)} %`}</span>
          <span className="stat__label">Personalkostenquote</span>
        </div>
      </div>

      {!vollstaendig && (
        <Banner tone="warning">
          Für mindestens eine eingeteilte Person ist kein Stundenlohn hinterlegt. Es wird deshalb{' '}
          <strong>keine</strong> Summe gezeigt — eine unvollständige sähe vollständig aus.
        </Banner>
      )}

      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Tag</th>
              <th>Stunden</th>
              <th>Kosten</th>
              <th>Umsatz</th>
              <th>Quote</th>
            </tr>
          </thead>
          <tbody>
            {tage.map((tag) => (
              <tr key={tag.day}>
                <td data-label="Tag">
                  {new Date(tag.day).toLocaleDateString('de-DE', {
                    weekday: 'short',
                    day: '2-digit',
                    month: '2-digit',
                  })}
                </td>
                <td data-label="Stunden">{Number(tag.planned_hours).toFixed(1)}</td>
                <td data-label="Kosten">
                  {tag.planned_cost === null ? (
                    <span className="muted" title={`${tag.missing_wages} ohne Stundenlohn`}>
                      –
                    </span>
                  ) : (
                    `${Number(tag.planned_cost).toFixed(2)} €`
                  )}
                </td>
                <td data-label="Umsatz">
                  {editing === tag.day ? (
                    <input
                      className="shell__select"
                      type="number"
                      min={0}
                      step="0.01"
                      autoFocus
                      value={wert}
                      onChange={(event) => setWert(event.target.value)}
                      onBlur={() => speichern.mutate({ tag: tag.day, betrag: wert })}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          speichern.mutate({ tag: tag.day, betrag: wert })
                        }
                        if (event.key === 'Escape') setEditing(null)
                      }}
                    />
                  ) : (
                    <Button
                      variant="text"
                      small
                      onClick={() => {
                        setEditing(tag.day)
                        setWert(tag.planned_revenue ?? '')
                      }}
                    >
                      {tag.planned_revenue
                        ? `${Number(tag.planned_revenue).toFixed(2)} €`
                        : 'eintragen'}
                    </Button>
                  )}
                </td>
                <td data-label="Quote">{tag.cost_ratio === null ? '–' : `${tag.cost_ratio} %`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="muted" style={{ fontSize: '0.8125rem', marginBottom: 0 }}>
        Alles <strong>Plan-Soll</strong>, nicht Ist — die Zeiterfassung kommt in der nächsten
        Ausbaustufe. Sichtbar nur für dich als Inhaber.
      </p>
    </Card>
  )
}

// ------------------------------------------------------- Schicht im Detail

/**
 * Was hinter einem Klick auf einen Kalendertermin steckt.
 *
 * Der Kalender zeigt aus Platzgründen nur Namen und Uhrzeit. Wer eine Schicht
 * anklickt, will wissen, wer eingeteilt ist und was fehlt — und im Zweifel
 * jemanden wieder herausnehmen. Ohne diesen Dialog wäre der Kalender eine
 * Anzeige, aus der man in die Rasteransicht wechseln müsste, um zu handeln.
 */
function ShiftDetailDialog({
  shift,
  locations,
  positions,
  canPlan,
  onClose,
  onUnassign,
}: {
  shift: Shift | null
  locations: Location[]
  positions: Position[]
  canPlan: boolean
  onClose: () => void
  onUnassign: (assignmentId: string) => void
}) {
  // Ohne Schicht gar nichts: Ein Dialog mit `open={false}` bräuchte trotzdem
  // Inhalt, und den gibt es hier nicht.
  if (!shift) return null

  const ort = locations.find((l) => l.id === shift.location_id)?.name ?? '?'
  const position = positions.find((p) => p.id === shift.position_id)?.name ?? '?'
  const fehlen = shift.min_staff - shift.assignments.length
  const beginn = new Date(shift.starts_at)
  const ende = new Date(shift.ends_at)

  return (
    <Dialog
      open
      onClose={onClose}
      title={beginn.toLocaleDateString('de-DE', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
      })}
    >
      <div className="stack">
        <div className="row row--between">
          <strong>
            {beginn.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} –{' '}
            {ende.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
          </strong>
          {shift.is_published ? (
            <Chip tone="success">veröffentlicht</Chip>
          ) : (
            <Chip tone="warning">Entwurf</Chip>
          )}
        </div>

        <p className="muted" style={{ margin: 0 }}>
          {position} · {ort}
          {shift.break_minutes > 0 && ` · ${shift.break_minutes} min Pause`}
        </p>

        {shift.note && <Banner tone="info">{shift.note}</Banner>}

        {shift.assignments.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>Noch niemand eingeteilt.</p>
        ) : (
          <ul className="stack stack--tight" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {shift.assignments.map((zuweisung) => (
              <li key={zuweisung.id} className="row row--between">
                <span>
                  {zuweisung.full_name}
                  {zuweisung.override_reason && (
                    <span className="muted"> · trotz Warnung: {zuweisung.override_reason}</span>
                  )}
                </span>
                {canPlan && (
                  <Button variant="text" small onClick={() => onUnassign(zuweisung.id)}>
                    Herausnehmen
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {fehlen > 0 && (
          <Banner tone="warning">
            Es {fehlen > 1 ? 'fehlen' : 'fehlt'} noch {fehlen} von {shift.min_staff}.
            {canPlan && ' In der Ansicht „Raster" lässt sich jemand zuweisen.'}
          </Banner>
        )}

        <div className="row row--between">
          <span />
          <Button variant="text" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
